import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Lock, ArrowLeft, MapPin, CreditCard, Building2, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const PHONE_RE = /^\+?[\d\s.\-()]{8,20}$/;
const PICKUP_ADDRESS = '17 rue du Dépôt, 01500 Ambérieu-en-Bugey';

const Checkout = () => {
  const { cart } = useCart();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    paymentMethod: 'stripe',
    notes: '',
  });

  // Prefill from logged user
  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        customerName: user.name || f.customerName,
        customerEmail: user.email || f.customerEmail,
        customerPhone: user.phone || f.customerPhone,
      }));
    }
  }, [user]);

  // Wait for auth check before redirecting
  if (authLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-gray-500">Chargement…</div>
    );
  }
  if (!user) {
    return <Navigate to="/connexion" replace state={{ from: '/checkout' }} />;
  }

  const isEmpty = !cart || !cart.items || cart.items.length === 0;
  if (isEmpty) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Votre panier est vide.</p>
          <button onClick={() => navigate('/produits')} className="bg-[#E42313] text-white px-6 py-3 rounded hover:bg-[#C11A0F]">
            Voir nos produits
          </button>
        </div>
      </div>
    );
  }

  const validate = () => {
    if (!form.customerName.trim() || form.customerName.trim().length < 2) return 'Nom invalide';
    if (!EMAIL_RE.test(form.customerEmail)) return 'Format email invalide';
    if (!PHONE_RE.test(form.customerPhone)) return 'Numéro de téléphone invalide';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/checkout/session`, {
        customerName: form.customerName.trim(),
        customerEmail: form.customerEmail.trim(),
        customerPhone: form.customerPhone.trim(),
        paymentMethod: form.paymentMethod,
        notes: form.notes || null,
        originUrl: window.location.origin,
      });

      if (data.type === 'transfer') {
        // Bank transfer: redirect to confirmation page with bank details
        navigate('/paiement-succes', {
          state: {
            type: 'transfer',
            order_number: data.order_number,
            amount: data.amount,
            iban: data.iban,
            bic: data.bic,
            bank: data.bank,
            holder: data.holder,
          },
        });
        return;
      }

      // Stripe
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("Pas d'URL de paiement reçue");
      }
    } catch (err) {
      const msg = err.response?.data?.detail;
      const text = typeof msg === 'string' ? msg : Array.isArray(msg) ? msg.map((m) => m.msg || JSON.stringify(m)).join(' ') : err.message;
      setError(text || 'Erreur lors de la commande');
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white min-h-[80vh]">
      <section className="bg-gray-900 text-white py-10">
        <div className="container mx-auto px-4">
          <button onClick={() => navigate('/panier')} className="flex items-center gap-2 text-gray-300 hover:text-white mb-3 text-sm">
            <ArrowLeft className="w-4 h-4" /> Retour au panier
          </button>
          <h1 className="text-4xl font-bold">Finaliser la commande</h1>
        </div>
      </section>

      <section className="container mx-auto px-4 py-10">
        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-testid="checkout-form">
          <div className="lg:col-span-2 space-y-6">
            {/* Pickup info banner */}
            <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded flex items-start gap-3">
              <MapPin className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <strong>Retrait sur place uniquement</strong>
                <div className="mt-1">
                  Vous récupérerez votre commande à : <strong>{PICKUP_ADDRESS}</strong>
                </div>
                <div className="text-xs text-amber-700 mt-1">La livraison n'est pas disponible pour le moment.</div>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-start gap-2" data-testid="checkout-error">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Contact */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h2 className="text-xl font-bold mb-4">Vos coordonnées</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Nom complet *</label>
                  <input
                    data-testid="checkout-name"
                    required
                    minLength={2}
                    value={form.customerName}
                    onChange={(e) => setForm({ ...form, customerName: e.target.value })}
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:border-[#E42313]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email *</label>
                  <input
                    data-testid="checkout-email"
                    type="email"
                    required
                    pattern="[^\s@]+@[^\s@]+\.[^\s@]+"
                    value={form.customerEmail}
                    onChange={(e) => setForm({ ...form, customerEmail: e.target.value })}
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:border-[#E42313]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Téléphone *</label>
                  <input
                    data-testid="checkout-phone"
                    type="tel"
                    required
                    pattern="[+\d\s().-]{8,20}"
                    placeholder="06 12 34 56 78 ou +33…"
                    value={form.customerPhone}
                    onChange={(e) => setForm({ ...form, customerPhone: e.target.value })}
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:border-[#E42313]"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Note pour le retrait (optionnel)</label>
                  <textarea
                    rows={2}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Précisions, RAL spécifique pour produits 'Autre RAL', date souhaitée…"
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:border-[#E42313]"
                  />
                </div>
              </div>
            </div>

            {/* Payment method */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h2 className="text-xl font-bold mb-4">Mode de paiement</h2>
              <div className="space-y-3">
                <label
                  data-testid="payment-stripe"
                  className={`flex items-start gap-3 p-4 border-2 rounded cursor-pointer transition-all ${
                    form.paymentMethod === 'stripe'
                      ? 'border-[#E42313] bg-red-50/30'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="pm"
                    value="stripe"
                    checked={form.paymentMethod === 'stripe'}
                    onChange={() => setForm({ ...form, paymentMethod: 'stripe' })}
                    className="mt-1 accent-[#E42313]"
                  />
                  <CreditCard className="w-6 h-6 text-gray-700 mt-0.5" />
                  <div>
                    <div className="font-semibold">Carte bancaire / PayPal</div>
                    <div className="text-xs text-gray-500">Paiement sécurisé en ligne via Stripe</div>
                  </div>
                </label>
                <label
                  data-testid="payment-transfer"
                  className={`flex items-start gap-3 p-4 border-2 rounded cursor-pointer transition-all ${
                    form.paymentMethod === 'transfer'
                      ? 'border-[#E42313] bg-red-50/30'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="pm"
                    value="transfer"
                    checked={form.paymentMethod === 'transfer'}
                    onChange={() => setForm({ ...form, paymentMethod: 'transfer' })}
                    className="mt-1 accent-[#E42313]"
                  />
                  <Building2 className="w-6 h-6 text-gray-700 mt-0.5" />
                  <div>
                    <div className="font-semibold">Virement bancaire</div>
                    <div className="text-xs text-gray-500">
                      Notre RIB vous sera communiqué à la commande. La marchandise sera préparée dès réception du virement.
                    </div>
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 sticky top-24">
              <h2 className="text-xl font-bold mb-4">Votre commande</h2>
              <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
                {cart.items.map((item) => (
                  <div key={item.productId} className="flex items-start gap-3 pb-3 border-b border-gray-200 last:border-0">
                    <img src={item.image} alt={item.name} className="w-14 h-14 object-cover rounded" />
                    <div className="flex-1 text-sm">
                      <div className="font-medium text-gray-900 line-clamp-2">{item.name}</div>
                      <div className="text-gray-500 text-xs">{item.quantity} ml × {item.price.toFixed(2)} €/ml</div>
                    </div>
                    <div className="text-sm font-semibold">{(item.price * item.quantity).toFixed(2)} €</div>
                  </div>
                ))}
              </div>
              <div className="space-y-1 text-sm mb-3">
                <div className="flex justify-between text-gray-700">
                  <span>Sous-total</span>
                  <span>{cart.totalAmount.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between text-gray-700">
                  <span>Retrait sur place</span>
                  <span className="text-green-600 font-semibold">Gratuit</span>
                </div>
              </div>
              <div className="flex justify-between text-lg font-bold border-t pt-3 mb-5">
                <span>Total TTC</span>
                <span className="text-[#E42313]" data-testid="checkout-total">{cart.totalAmount.toFixed(2)} €</span>
              </div>
              <button
                data-testid="checkout-submit"
                type="submit"
                disabled={submitting}
                className="w-full bg-[#E42313] text-white py-4 rounded font-semibold hover:bg-[#C11A0F] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Lock className="w-4 h-4" />
                {submitting
                  ? 'Traitement…'
                  : form.paymentMethod === 'stripe'
                    ? 'Payer en toute sécurité'
                    : 'Valider la commande (virement)'}
              </button>
              <p className="text-xs text-center text-gray-500 mt-3">
                {form.paymentMethod === 'stripe' ? 'Paiement sécurisé via Stripe' : 'Vous recevrez le RIB par email'}
              </p>
            </div>
          </div>
        </form>
      </section>
    </div>
  );
};

export default Checkout;
